import auth0 from 'auth0-js';
import { logger } from 'common/utils/helpers';
import { auth0Config } from './config';

const debug = false;
const log = (message) => {
  if (debug) {
    logger.log(message);
  }
};

/**
 * Component: Auth0 implements the security features to access the cloud information (based on JWTs)
 *
 * @class Auth0
 * @file Auth0.js
 * @author Christophe Vidal
 *
 */
export default class Auth0 {
  /**
   *  Fields: public, to store the tokens
   *
   */
  accessToken;
  idToken;
  expiresAt;

  constructor() {
    this.accessToken = null;
    this.idToken = null;
    this.expiresAt = 0;
  }

  /**
   * auth0: object created with the credentials to request the tokens (JWTs) from the cloud
   *
   * @property auth0
   * @type {object}
   */
  auth0 = new auth0.WebAuth(auth0Config);

  async getToken() {
    if (this.renewPromise != null) {
      try {
        await this.renewPromise;
      } catch (error) {}
    }

    return this.accessToken;
  }

  /**
   * login: request the tokens
   *
   * @method login
   * @public
   *
   */
  login = () => {
    this.auth0.authorize({ prompt: 'login' });
  };

  embeddedLogin = (username, password, cb) => {
    this.auth0.login(
      {
        username: username,
        password: password,
        realm: 'Username-Password-Authentication'
      },
      cb
    );
  };

  resetPassword = (email, cb) => {
    this.auth0.changePassword(
      {
        email: email,
        connection: 'Username-Password-Authentication'
      },
      cb
    );
  };

  getUserInfo = (cb) => {
    if (this.accessToken === null) {
      return cb('null token', null);
    }
    try {
      this.auth0.client.userInfo(this.accessToken, cb);
    } catch (error) {
      return cb(error, null);
    }
  };

  /**
   * logout: remove the authorization items from the local storage (accessToken, idToken, expiresAt)
   *
   * @method logout
   * @public
   *
   */
  logout = () => {
    log('Auth0 - Logging out');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('expiresAt');
    localStorage.removeItem('deviceColumnWidth'); // removed the storge width item once user logs out
    localStorage.removeItem('podColumnWidth'); // removed the storge width item once user logs out
    log('accessToken LS = ' + localStorage.getItem('accessToken'));
    log('idToken LS = ' + localStorage.getItem('idToken'));
    log('expiresAt LS = ' + localStorage.getItem('expiresAt'));
    log('isAuthenticated LS = ' + this.isAuthenticatedLS());
    this.accessToken = null;
    this.idToken = null;
    this.expiresAt = 0;
    log('accessToken CF = ' + localStorage.getItem('accessToken'));
    log('idToken CF = ' + localStorage.getItem('idToken'));
    log('expiresAt CF = ' + localStorage.getItem('expiresAt'));
    log('isAuthenticated = ' + this.isAuthenticated());
  };

  /**
   * handleAuthentication manage the authentication process
   *
   * Calls: setSession (when needed)
   *
   * @method handleAuthentication
   * @public
   *
   */
  handleAuthentication = () => {
    return new Promise((resolve, reject) => {
      this.auth0.parseHash((err, authResult) => {
        if (authResult && authResult.accessToken && authResult.idToken) {
          this.setSession(authResult);
          resolve(authResult);
        } else if (err) {
          logger.log('Auth0 hA: ', err);
          reject(err);
        } else {
          logger.log('Authentication problem: no tokens, no error (Auth0 hA else)');
          reject(new Error('Authentication problem'));
        }
      });
    });
  };

  /**
   * isAuthenticated check if the user has been authenticated
   *
   * @method isAuthenticated
   * @return: {boolean} true if the token expiration dates has not been passed yet, false otherwise
   *
   */
  isAuthenticatedLS = () => {
    log(
      'isAuthenticated LS - time = ' +
        new Date().getTime() +
        ' vs expiration timer = ' +
        localStorage.getItem('expiresAt')
    );
    if (
      new Date().getTime() < localStorage.getItem('expiresAt') &&
      localStorage.getItem('accessToken') != null &&
      localStorage.getItem('idToken') != null
    ) {
      if (!this.accessToken) {
        this.accessToken = localStorage.getItem('accessToken');
      }
      if (!this.idToken) {
        this.idToken = localStorage.getItem('idToken');
      }

      return true;
    } else {
      return false;
    }
  };

  isAuthenticated = () => {
    log('isAuthenticated - time = ' + new Date().getTime() + ' vs expiration timer = ' + this.expiresAt);
    log('accessToken = ' + this.accessToken);
    log('idToken = ' + this.idToken);
    return new Date().getTime() < this.expiresAt;
  };

  /**
   *
   * setSession opens the session by storing in the local storage the authentication credentials (accessToken, idToken, expiresAt)
   *
   * @method setSession
   * @param {object} authResult
   *
   */
  setSession = (authResult) => {
    log('setSesstion authResult = ' + JSON.stringify(authResult));
    log('setSesstion accessToken = ' + authResult.accessToken);
    log('setSesstion idToken = ' + authResult.idToken);
    log('setSesstion expiresIn = ' + authResult.expiresIn);
    localStorage.setItem('accessToken', authResult.accessToken);
    localStorage.setItem('idToken', authResult.idToken);
    localStorage.setItem('expiresAt', authResult.expiresIn * 1000 + new Date().getTime());
    log(
      'setSession expiration timer LS = ' +
        localStorage.getItem('expiresAt') +
        ' | ' +
        new Date(localStorage.getItem('expiresAt'))
    );
    log('setSession isAuthenticated LS = ' + this.isAuthenticatedLS());
    this.accessToken = authResult.accessToken;
    this.idToken = authResult.idToken;
    //this.expiresAt = authResult.expiresIn * 1000 + new Date().getTime();
    /* Redirection to the Home page */
    log('setSession expiration timer = ' + this.expiresAt + ' | ' + new Date(this.expiresAt));
    log('setSession isAuthenticated = ' + this.isAuthenticated());
  };

  isTokenApproachingExpiry = () => {
    //1800000 is 30 mins in epock time
    let halfHourBeforeExpiry = localStorage.getItem('expiresAt') - 1800000;

    return new Date().getTime() > halfHourBeforeExpiry;
  };

  /*
   * renewSession : calls setSession when the tokens are still valid, log out the user otherwise
   *
   * Calls: setSession, logout
   *
   * @method renewSession'
   * @param {none} none
   *
   */
  renewSession = () => {
    if (this.renewPromise == null) {
      this.renewPromise = new Promise((resolve, reject) => {
        this.auth0.checkSession({ sameSite: 'strict' }, (err, authResult) => {
          if (authResult && authResult.accessToken && authResult.idToken) {
            this.setSession(authResult);
            resolve();
            this.renewPromise = null;
            return;
          } else if (err) {
            logger.log('failed to renew, err:', err);
            alert('Your session has timed out. Please log in again');
          } else {
            logger.log('Cannot renew session: no tokens, no error (Auth0 rS else)');
          }
          reject();
          this.renewPromise = null;
        });
      });
    }

    return this.renewPromise;
  };
}
